// File: src/app-telehealth-demo/components/RemoteCareDemo.tsx

import axios from "axios";
import React, { useState } from "react";
import { Alert, Button, Card, Col, Container, ProgressBar, Row } from "react-bootstrap";
import {
    IConversion,
    ILocationInfo,
    IMedicalQuestionData,
    IPatientInfo,
    MEDICAL_QUESTIONS,
    getApiHeaders,
    getApiUrl,
} from "../util/utils";
import RemoteCareDemoSettingsModal from "./RemoteCareDemoSettingsModal";
import LocationStep from "./steps/LocationStep";
import MedicalQuestionsStep from "./steps/MedicalQuestionsStep";
import OrderConfirmationStep from "./steps/OrderConfirmationStep";
import PatientInfoStep from "./steps/PatientInfoStep";
import PatientRemoteCareConversionStep from "./steps/RemoteCareConversionStep";
import ReviewAndSubmitStep from "./steps/ReviewAndSubmitStep";
import SelfSchedulingStep from "./steps/SelfSchedulingStep";
import WelcomeStep from "./steps/WelcomeStep";

export interface IFormData {
    locationInfo: ILocationInfo;
    patientInfo: IPatientInfo;
    prefillFields: IMedicalQuestionData;
    selectedServiceIds: number[];
}

const DEFAULT_FORM_DATA: IFormData = {
    locationInfo: {
        address: "",
        city: "",
        state: "",
        zip: "",
    },
    patientInfo: {
        firstName: "",
        lastName: "",
        dateOfBirth: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        state: "",
        zip: "",
    },
    prefillFields: {},
    selectedServiceIds: [],
};

// Total number of steps in the workflow (decreased from 7 to 6 after removing billing step)
const TOTAL_STEPS = 6;

const RemoteCareDemo: React.FC = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState<IFormData>(DEFAULT_FORM_DATA);
    const [conversion, setConversion] = useState<IConversion | null>(null);
    const [actionId, setActionId] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showSettings, setShowSettings] = useState(false);
    const [orderCompleted, setOrderCompleted] = useState(false);
    const [orderDetails, setOrderDetails] = useState<{
        submitData: {
            firstName: string;
            lastName: string;
            email: string;
            phone: string;
            address: string;
            address2?: string;
            city: string;
            state: string;
            zip: string;
        };
        services: {
            id: string;
            careServiceName: string;
            costInCents: number;
            requiresLab: boolean;
        }[];
    } | null>(null);

    const updateFormData = <K extends keyof IFormData>(
        section: K,
        data: Partial<IFormData[K]>,
        replace?: boolean,
    ) => {
        setFormData((prev) => {
            if (Array.isArray(prev[section])) {
                return {
                    ...prev,
                    [section]: data,
                };
            }
            return {
                ...prev,
                [section]: replace
                    ? data
                    : {
                          ...(prev[section] as object),
                          ...data,
                      },
            };
        });
    };

    const handleNext = () => {
        setCurrentStep(Math.min(currentStep + 1, TOTAL_STEPS));
    };

    const handleBack = () => {
        setCurrentStep(Math.max(currentStep - 1, 0));
    };

    const scheduleNow = () => {
        setCurrentStep(8);
    };

    // Reset the entire workflow
    const handleReset = () => {
        setCurrentStep(0);
        setFormData(DEFAULT_FORM_DATA);
        setConversion(null);
        setActionId(null);
        setOrderCompleted(false);
        setOrderDetails(null);
    };

    // Handle medical question data
    const handleMedicalQuestionsSubmit = (prefillFields: IMedicalQuestionData) => {
        // Just collect and store the data, then move to the next step
        updateFormData("prefillFields", prefillFields, true);
        handleNext();
    };

    // Function to submit the conversion from the review step
    const submitConversion = async () => {
        if (!conversion) return;

        setIsSubmitting(true);
        setError(null);

        try {
            const selectedServices = conversion.services
                .filter((service) => formData.selectedServiceIds.includes(service.careServiceId))
                .map((service) => service.careServiceId);

            const submitData = {
                conversionTrackingUuid: conversion.conversionTrackingUuid,
                // Patient info
                firstName: formData.patientInfo.firstName,
                lastName: formData.patientInfo.lastName,
                dateOfBirth: formData.patientInfo.dateOfBirth,
                email: formData.patientInfo.email,
                phone: formData.patientInfo.phone,
                address: formData.patientInfo.address,
                address2: formData.patientInfo.address2 || null,
                city: formData.patientInfo.city,
                state: formData.patientInfo.state,
                zip: formData.patientInfo.zip,
                // Selected services
                careServiceIds: selectedServices,
                prefillFields: formData.prefillFields,
            };

            const response = await axios.post(getApiUrl("submit-conversion"), submitData, {
                headers: getApiHeaders(),
            });

            // Parse response to get action ID
            try {
                const responseData = response.data;
                if (responseData.paymentUrl) {
                    // Extract actionId from paymentUrl
                    const url = new URL(responseData.paymentUrl);
                    const params = new URLSearchParams(url.search);
                    const extractedActionId = params.get("actionId");
                    setActionId(extractedActionId);

                    // Store submit data for payment step
                    setOrderDetails({
                        submitData,
                        services: conversion.services.filter((service) =>
                            formData.selectedServiceIds.includes(service.careServiceId),
                        ),
                    });

                    // Move to payment step directly (skipping billing step)
                    handleNext();
                } else {
                    throw new Error("No payment URL returned");
                }
            } catch (parseError) {
                console.error("Error parsing response:", parseError);
                setError("Failed to process your information. Please try again.");
            }
        } catch (error) {
            console.error("Error submitting conversion:", error);
            setError("Failed to process your information. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePaymentComplete = () => {
        // Mark the order as completed
        setOrderCompleted(true);
        // Move to confirmation step
        setCurrentStep(6);
    };

    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return <WelcomeStep onContinue={handleNext} />;
            case 1:
                return (
                    <LocationStep
                        selectedServiceIds={formData.selectedServiceIds}
                        updateSelectedServiceIds={(data) =>
                            updateFormData("selectedServiceIds", data)
                        }
                        locationInfo={formData.locationInfo}
                        updateLocationInfo={(data) => updateFormData("locationInfo", data)}
                        conversion={conversion}
                        updateConversion={setConversion}
                        onContinue={handleNext}
                        onBack={handleBack}
                        scheduleAppointment={scheduleNow}
                    />
                );
            case 2:
                return (
                    <PatientInfoStep
                        locationInfo={formData.locationInfo}
                        patientInfo={formData.patientInfo}
                        updatePatientInfo={(data) => updateFormData("patientInfo", data)}
                        onContinue={handleNext}
                        onBack={handleBack}
                    />
                );
            case 3:
                return (
                    <MedicalQuestionsStep
                        questions={MEDICAL_QUESTIONS}
                        prefillFields={formData.prefillFields}
                        updatePrefillFields={(data) => updateFormData("prefillFields", data, true)}
                        onContinue={handleNext}
                        onBack={handleBack}
                        isSubmitting={false}
                        error={error}
                        onSubmit={handleMedicalQuestionsSubmit}
                    />
                );
            case 4:
                return (
                    <ReviewAndSubmitStep
                        formData={formData}
                        conversion={conversion}
                        onContinue={submitConversion}
                        onBack={handleBack}
                        isSubmitting={isSubmitting}
                        error={error}
                    />
                );
            case 5:
                return actionId ? (
                    <PatientRemoteCareConversionStep
                        actionId={actionId}
                        orderDetails={orderDetails}
                        onPaymentComplete={handlePaymentComplete}
                        onBack={handleBack}
                    />
                ) : (
                    <div className="text-center py-5">
                        <Alert variant="danger">
                            Payment information not available. Please go back to the previous steps
                            and try again.
                        </Alert>
                        <Button variant="secondary" onClick={handleBack} className="mt-3">
                            Back
                        </Button>
                    </div>
                );
            case 6:
                if (!orderDetails) return null;
                return (
                    <OrderConfirmationStep
                        conversion={conversion}
                        orderDetails={orderDetails}
                        onFinish={handleReset}
                    />
                );
            case 8:
                return <>{conversion && <SelfSchedulingStep conversion={conversion} />}</>;
            default:
                return <WelcomeStep onContinue={handleNext} />;
        }
    };

    const progress = (currentStep / TOTAL_STEPS) * 100;

    return (
        <div className="telehealth-demo">
            <header className="telehealth-header">
                <Container>
                    <div className="d-flex justify-content-between align-items-center py-3">
                        <img
                            src="https://gamedaymenshealth.com/wp-content/uploads/2024/09/GD-Header-black.png"
                            alt="Lobbie Remote Care"
                            height={50}
                            className="logo"
                        />
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setShowSettings(true)}
                            className="settings-button"
                        >
                            <i className="bi bi-gear-fill me-1"></i> Settings
                        </Button>
                    </div>
                    {currentStep !== 8 && (
                        <div className="progress-container">
                            <ProgressBar now={progress} className="mb-4" />
                            <div className="text-end small mb-2">
                                Step {currentStep} of {TOTAL_STEPS}
                            </div>
                        </div>
                    )}
                </Container>
            </header>
            <Container className="main-content">
                <Row className="justify-content-center">
                    <Col md={10} lg={8}>
                        <Card className="main-card shadow">
                            <Card.Body>{renderStep()}</Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <footer className="footer">
                <Container>
                    <p className="text-center text-muted">
                        © {new Date().getFullYear()} Lobbie Inc. All rights reserved.
                    </p>
                </Container>
            </footer>
            <RemoteCareDemoSettingsModal
                show={showSettings}
                onHide={() => setShowSettings(false)}
            />
        </div>
    );
};

export default RemoteCareDemo;
